import { CalendarX, Check, MicOff } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import { m } from '@/paraglide/messages.js';

export interface DisconnectCalendarDialogProps {
  /** The calendar about to be unlinked, by name. Null closes the dialog. */
  providerName: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The confirm in front of unlinking a calendar.
 *
 * It informs rather than asks a second question: disconnecting keeps everything
 * already captured, so there is no keep-or-delete choice to offer. What it owes
 * the user is the shape of the aftermath — what stops, what stays, and the one
 * thing that does disappear — because the fear that stops someone pressing this
 * button is losing work they cannot get back.
 */
export function DisconnectCalendarDialog({
  providerName,
  onConfirm,
  onCancel,
}: DisconnectCalendarDialogProps) {
  return (
    <Dialog open={providerName !== null} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>
            {m.connections_disconnect_title({ provider: providerName ?? '' })}
          </DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-3.5 px-5 py-4'>
          <DialogDescription>{m.connections_disconnect_intro()}</DialogDescription>

          {/*
           * Three consequences, in the order they weigh on the decision: what
           * stops, then the reassurance, then the single loss. The middle line
           * carries the tick because it is the answer to the unasked question.
           */}
          <ul className='flex flex-col gap-2.5 text-sm text-ink-body'>
            <li className='flex items-start gap-2.5'>
              <MicOff
                className='mt-0.5 size-4 shrink-0 text-ink-muted'
                aria-hidden='true'
              />
              <span>{m.connections_disconnect_notetaker()}</span>
            </li>
            <li className='flex items-start gap-2.5'>
              <Check className='mt-0.5 size-4 shrink-0 text-success' aria-hidden='true' />
              <span>{m.connections_disconnect_kept()}</span>
            </li>
            <li className='flex items-start gap-2.5'>
              <CalendarX
                className='mt-0.5 size-4 shrink-0 text-ink-muted'
                aria-hidden='true'
              />
              <span>{m.connections_disconnect_planned()}</span>
            </li>
          </ul>
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onCancel}>
            {m.connections_cancel()}
          </Button>
          <Button size='sm' variant='danger' onClick={onConfirm}>
            {m.connections_disconnect()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
